/*
 * Keeps the cart note (copied by Shopify into the order note) in sync with the
 * bundles in the cart, so admin and the API see what each bundle contains.
 *
 * Each bundle line carries the readable `_bundle_continut` property written by
 * annette-product-bundle-builder.js. After every cart change the note's bundle
 * block is rebuilt from the current cart; text outside the block is kept.
 */
(() => {
    if (window.__bundleOrderNoteReady) return;
    window.__bundleOrderNoteReady = true;

    const START = '=== Pachete ===';
    const END = '=== Sfârșit pachete ===';
    const CART_CHANGE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const nativeFetch = window.fetch.bind(window);
    const config = window.bundleOrderNote || {};

    let timer = null;
    let running = false;
    let queued = false;

    const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const blockPattern = new RegExp(`\\s*${escapeRegExp(START)}[\\s\\S]*?${escapeRegExp(END)}\\s*`, 'g');

    // Same layout as BundleBuilder#buildContentsText in annette-product-bundle-builder.js.
    function formatContents(roles) {
        const hasFree = roles.free.length > 0;
        const groups = [
            ['paid', hasFree ? 'Plătite' : 'Produse'],
            ['free', 'GRATIS'],
            ['gift', 'Cadou']
        ];

        return groups
            .filter(([role]) => roles[role].length)
            .map(([role, label]) => {
                const count = roles[role].reduce((total, entry) => total + entry.quantity, 0);
                const heading = role === 'gift' ? `${label}:` : `${label} (${count} buc):`;
                // "SKU: 1 × Product name"; expanded lines only carry the full product name.
                const lines = roles[role].map((entry) => `${entry.sku ? `${entry.sku}: ` : ''}${entry.quantity} × ${entry.title}`);
                return [heading, ...lines].join('\n');
            })
            .join('\n');
    }

    // The Cart Transform (lineExpand) replaces a bundle line with its components,
    // each tagged with _bundle_id, _bundle_set, _bundle_name and _bundle_role.
    // Unexpanded bundle lines still carry _bundle_continut from the builder.
    // Shopify Bundles lines only have has_components; their components come
    // from the cart.bundle-components view, keyed by line key.
    function collectBundles(items, components = {}) {
        const bundles = [];
        const expanded = new Map();

        items.forEach((item) => {
            const properties = item.properties || {};

            if (properties._bundle_continut) {
                const quantity = item.quantity > 1 ? ` (×${item.quantity})` : '';
                bundles.push({
                    heading: `${properties._bundle_title || item.product_title}${quantity}`,
                    contents: properties._bundle_continut
                });
                return;
            }

            if (item.has_components) {
                const parts = components[item.key];
                if (!parts || !parts.length) return;

                const quantity = item.quantity > 1 ? ` (×${item.quantity})` : '';
                // Component quantities already include the bundle quantity.
                const roles = { paid: parts.map((part) => ({ title: part.title, sku: part.sku || '', quantity: part.quantity })), free: [], gift: [] };
                bundles.push({ heading: `${item.product_title}${quantity}`, contents: formatContents(roles) });
                return;
            }

            if (!properties._bundle_id || !properties._bundle_role) return;

            const key = `${properties._bundle_id}|${properties._bundle_set || ''}`;
            if (!expanded.has(key)) {
                expanded.set(key, { heading: properties._bundle_name || 'Pachet', roles: { paid: [], free: [], gift: [] } });
                bundles.push(expanded.get(key));
            }

            const roles = expanded.get(key).roles;
            const role = roles[properties._bundle_role] ? properties._bundle_role : 'paid';
            roles[role].push({ title: item.product_title, sku: item.sku || '', quantity: item.quantity });
        });

        return bundles.map((bundle) => (bundle.contents ? bundle : { heading: bundle.heading, contents: formatContents(bundle.roles) }));
    }

    function buildBlock(items, components) {
        const bundles = collectBundles(items, components);
        if (!bundles.length) return '';

        const parts = bundles.map((bundle, index) => `Pachet ${index + 1} – ${bundle.heading}\n${bundle.contents}`);

        return `${START}\n${parts.join('\n\n')}\n${END}`;
    }

    function mergeNote(note, block) {
        const rest = (note || '').replace(blockPattern, '\n\n').trim();
        return [rest, block].filter(Boolean).join('\n\n');
    }

    // Discount on Cart Pro re-sends the note it reads from textarea[name="note"]
    // and would send an empty one without it; mirror the real note there.
    function mirror(note) {
        let field = document.getElementById('BundleOrderNoteMirror');
        if (!field) {
            field = document.createElement('textarea');
            field.id = 'BundleOrderNoteMirror';
            field.name = 'note';
            field.hidden = true;
            field.setAttribute('aria-hidden', 'true');
            field.tabIndex = -1;
            document.body.appendChild(field);
        }
        field.value = note;
    }

    // Without the components a Shopify bundle is left out of the note; the
    // custom bundles are still written.
    async function fetchComponents(items) {
        if (!items.some((item) => item.has_components)) return {};

        try {
            // No JSON Accept header: with it Shopify answers with /cart.js instead of the view.
            const response = await nativeFetch(`${root}cart?view=bundle-components`);
            const data = await response.json();
            return data.items || {};
        } catch (error) {
            return {};
        }
    }

    async function sync() {
        if (running) {
            queued = true;
            return;
        }
        running = true;

        try {
            const cart = await nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
            const items = cart.items || [];
            const current = cart.note || '';
            const note = mergeNote(current, buildBlock(items, await fetchComponents(items)));

            if (note !== current) {
                await nativeFetch(`${root}cart/update.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ note })
                });
            }

            mirror(note);
        } catch (error) {
            // A failed sync is retried on the next cart change or page load.
        } finally {
            running = false;
            if (queued) {
                queued = false;
                schedule();
            }
        }
    }

    function schedule() {
        clearTimeout(timer);
        timer = setTimeout(sync, 400);
    }

    const isCartChange = (url) => CART_CHANGE.test(String(url || ''));

    window.fetch = function (input, init) {
        const url = typeof input === 'string' ? input : input && input.url;
        const request = nativeFetch(input, init);

        if (isCartChange(url)) {
            request.then((response) => {
                if (response.ok) schedule();
            }).catch(() => {});
        }

        return request;
    };

    const nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
        if (isCartChange(url)) {
            this.addEventListener('load', () => {
                if (this.status < 400) schedule();
            });
        }
        return nativeOpen.apply(this, arguments);
    };

    const start = () => {
        if (config.check) {
            schedule();
        } else if (config.note) {
            mirror(config.note);
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
})();
